import './App.css';
import { useEffect, useRef, useState } from 'react';
import BootstrapMessaging from './bootstrapMessaging';
import AwacDemo from './awac/demo/AwacDemo';
import { t, useAwacLang } from './awac/i18n';
import { getAwacState, setAwacState, clearAwacState } from './awac/awacSessionState';
import { track, AwacEvents } from './awac/analytics';
import { registerMapFrame, unregisterMapFrame, setMapLanguage } from './awac/poiMapBridge';
import { setCurrentPage, setCurrentSection } from './awac/pageContext';
import { UNITS } from './unitsCatalog';

// Real inventory summary: the cheapest unit for each bed+bath combination,
// computed from the org's Advertesting_Unit__c snapshot (src/unitsData.json).
const CHEAPEST_BY_COMBO = (() => {
  const best = new Map();
  for (const u of UNITS) {
    if (!u.beds || !u.baths || !u.price) continue;
    const key = `${u.beds}-${u.baths}`;
    if (!best.has(key) || u.price < best.get(key).price) {
      best.set(key, u);
    }
  }
  return [...best.values()].sort((a, b) => a.beds - b.beds || a.baths - b.baths);
})();

/**
 * Ask the chat bootstrapper (which listens for this event) to open Lia.
 * Optionally hands off a page-context message: it is stored as the session
 * pendingMessage BEFORE the event fires, and the conversation layer sends it
 * once the conversation is open (or immediately if it already is).
 * @param {{message?: string, source?: string}} [options]
 */
function openChat(options) {
  const message = options && options.message;
  const source = (options && options.source) || 'page';
  if (message) {
    setAwacState('pendingMessage', message);
  }
  window.dispatchEvent(new CustomEvent('awac:open-chat', { detail: { source } }));
}

/** Format a CAD price for the active locale (fr-CA: 329 900 $ / en-CA: $329,900). */
function formatPrice(value, lang) {
  try {
    return new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
    }).format(value);
  } catch (e) {
    return `$${value.toLocaleString('en-CA')}`;
  }
}

/** Hand-built layered mountain ridge — Mont Saint-Hilaire silhouette. */
function Ridge() {
  return (
    <div className="libRidge" aria-hidden="true">
      <svg viewBox="0 0 1440 190" preserveAspectRatio="none" role="presentation">
        <path
          d="M0,150 L180,108 L320,132 L520,70 L700,118 L900,52 L1080,110 L1260,84 L1440,128 L1440,190 L0,190 Z"
          fill="oklch(85% 0.035 230)"
        />
        <path
          d="M0,170 L220,134 L420,156 L640,104 L860,148 L1060,96 L1240,138 L1440,114 L1440,190 L0,190 Z"
          fill="oklch(58% 0.07 232)"
        />
        <path
          d="M0,184 L260,158 L500,176 L760,140 L1020,170 L1260,150 L1440,166 L1440,190 L0,190 Z"
          fill="oklch(33% 0.06 232)"
        />
      </svg>
    </div>
  );
}

/** EN / FR toggle — English-default for the pilot (flip for a Bill 96 launch). */
function LanguageToggle() {
  const [lang, setLang] = useAwacLang();
  return (
    <div className="libLangToggle" role="group" aria-label="Language / Langue">
      <button
        type="button"
        className={'libLangBtn' + (lang === 'en' ? ' libLangBtnActive' : '')}
        aria-pressed={lang === 'en'}
        onClick={() => setLang('en')}>
        EN
      </button>
      <span className="libLangSep" aria-hidden="true">·</span>
      <button
        type="button"
        className={'libLangBtn' + (lang === 'fr' ? ' libLangBtnActive' : '')}
        aria-pressed={lang === 'fr'}
        onClick={() => setLang('fr')}>
        FR
      </button>
    </div>
  );
}

/** Phone-only hamburger menu (design handoff §9): ☰ in the header opens a
 * 210px dropdown card with the three pages; outside tap or navigation closes
 * it. Hidden on desktop, where the inline header links remain. */
function MobileNavPages({ active }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const pages = [
    { key: 'home', href: '#/', label: t('nav.tabHome') },
    { key: 'units', href: '#/units', label: t('nav.tabUnits') },
    { key: 'map', href: '#/neighborhood', label: t('nav.neighborhoodTab') }
  ];
  return (
    <div className="libNavMenu" ref={boxRef}>
      <button
        type="button"
        className="libNavMenuBtn"
        aria-label={t('nav.menu')}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">☰</span>
      </button>
      {open && (
        <div className="libNavMenuCard" role="menu">
          {pages.map((p) => (
            <a
              key={p.key}
              role="menuitem"
              href={p.href}
              className={active === p.key ? 'libNavMenuActive' : undefined}
              aria-current={active === p.key ? 'page' : undefined}
              onClick={() => setOpen(false)}>
              {p.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// Proactive nudge: appears once per session after 25s on the page OR once the
// visitor scrolls to the residences section — whichever comes first.
const NUDGE_DELAY_MS = 25000;

function ProactiveNudge() {
  useAwacLang();
  const [visible, setVisible] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (getAwacState('proactiveDismissed')) {
      return undefined;
    }

    function show() {
      if (firedRef.current) {
        return;
      }
      firedRef.current = true;
      setVisible(true);
      track(AwacEvents.PROACTIVE_SHOWN, {});
    }

    const timerId = setTimeout(show, NUDGE_DELAY_MS);

    function onScroll() {
      const section = document.getElementById('residences');
      if (section && section.getBoundingClientRect().top < window.innerHeight * 0.6) {
        show();
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    // If the visitor opens the chat on their own, the nudge is moot.
    function onChatOpened() {
      firedRef.current = true;
      setVisible(false);
      setAwacState('proactiveDismissed', true);
    }
    window.addEventListener('awac:open-chat', onChatOpened);

    return () => {
      clearTimeout(timerId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('awac:open-chat', onChatOpened);
    };
  }, []);

  if (!visible) {
    return null;
  }

  function handleEngage() {
    setVisible(false);
    setAwacState('proactiveDismissed', true);
    track(AwacEvents.PROACTIVE_ENGAGED, {});
    openChat({ message: t('nudge.message'), source: 'nudge' });
  }

  function handleDismiss() {
    setVisible(false);
    setAwacState('proactiveDismissed', true);
  }

  return (
    <div className="libNudge" role="dialog" aria-label={t('nudge.text')}>
      <p className="libNudgeText">{t('nudge.text')}</p>
      <div className="libNudgeActions">
        <button type="button" className="libNudgeCta" onClick={handleEngage}>
          {t('nudge.cta')}
        </button>
        <button type="button" className="libNudgeDismiss" onClick={handleDismiss}>
          {t('nudge.dismiss')}
        </button>
      </div>
    </div>
  );
}

function LandingPage() {
  const [lang] = useAwacLang();

  // Keep the document language attribute in sync (Bill 96: FR default).
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Navigation-aware agent: report which landing-page section is in view so
  // outgoing chat messages carry it (e.g. [[page:home/residences]]). Sections
  // are taller than the viewport, so IntersectionObserver ratio thresholds
  // don't work — instead pick the section covering the viewport's midpoint.
  useEffect(() => {
    const ids = ['residences', 'neighborhood'];
    function onScroll() {
      const mid = window.innerHeight * 0.5;
      let current = '';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= mid && rect.bottom >= mid) {
          current = id;
          break;
        }
      }
      setCurrentSection(current);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      setCurrentSection('');
    };
  }, []);

  return (
    <div className="lib">
      <nav className="libNav" aria-label="Main">
        <a className="libWordmark" href="/">
          Le Lib<span className="libWordmarkFull"> Mont-Saint-Hilaire</span>
        </a>
        <div className="libNavLinks">
          <a href="#/" className="libNavActive" aria-current="page">{t('nav.backHome')}</a>
          <a href="#/units">{t('nav.unitsTab')}</a>
          <a href="#/neighborhood">{t('nav.neighborhoodTab')}</a>
          <LanguageToggle />
          <button className="libBtn libBtnPrimary" onClick={() => openChat({ source: 'nav' })}>
            <span className="libChatFull">{t('nav.chat')}</span>
            <span className="libChatShort">{t('nav.chatShort')}</span>
          </button>
        </div>
        <MobileNavPages active="home" />
      </nav>

      <header className="libHero">
        {/* Hero: the original pilot drone loop of Mont Saint-Hilaire over the
            town — "at the foot of the mountain", literally. muted+playsInline
            lets iOS autoplay; the lelib montage sits beneath as poster and as
            the reduced-motion / autoplay-blocked fallback. Decorative; the
            scrim keeps text legible. */}
        <img
          className="libHeroVideo"
          src="lib/photo-montage-MSH3.png"
          alt=""
          aria-hidden="true"
        />
        <video
          className="libHeroVideo"
          src="media/hero-loop.mp4"
          poster="lib/photo-montage-MSH3.png"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
        <div className="libHeroScrim" aria-hidden="true" />
        <p className="libEyebrow">{t('hero.eyebrow')}</p>
        <h1>
          {t('hero.titlePre')}<em>{t('hero.titleEm')}</em>
        </h1>
        <p className="libHeroLede">{t('hero.lede')}</p>
        <div className="libHeroActions">
          <button className="libBtn libBtnPrimary" onClick={() => openChat({ source: 'hero' })}>
            {t('hero.chat')}
          </button>
          <a className="libBtn libBtnGhost" href="#residences">
            {t('hero.viewResidences')}
          </a>
        </div>
        <ul className="libHeroFacts" aria-label="Key facts">
          <li>{t('hero.fact1')}</li>
          <li>{t('hero.fact2')}</li>
          <li>{t('hero.fact3')}</li>
        </ul>
        <Ridge />
      </header>

      <main>
        <section className="libSection" id="residences">
          <div className="libSectionHead">
            <h2>{t('residences.title')}</h2>
            <p>{t('residences.lede')}</p>
          </div>
          <div className="libPlans">
            {CHEAPEST_BY_COMBO.map((u) => (
              <div className="libPlanRow" key={`${u.beds}-${u.baths}`}>
                <div className="libPlanName">
                  {u.beds} {t('plan.bed')} · {u.baths} {t('plan.bath')}
                </div>
                <div className="libPlanCell">
                  <strong>{t('unit.name', { unit: u.unit })}</strong>
                  {u.floor != null ? t('unit.floor', { floor: u.floor }) : t('plan.layout')}
                </div>
                <div className="libPlanCell">
                  <strong>
                    {u.sqft != null ? `${u.sqft.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')} ${t('plan.sqft')}` : '—'}
                  </strong>
                  {t('plan.interior')}
                </div>
                <div className="libPlanCell">
                  <strong>{formatPrice(u.price, lang)}{t('unit.perMonth')}</strong>
                  {t('plan.from')}
                </div>
                <button
                  className="libPlanAsk"
                  onClick={() => openChat({ message: t('unit.askMessage', { unit: u.unit }), source: 'plan_row' })}>
                  {t('plan.ask')}
                </button>
              </div>
            ))}
          </div>
          <p className="libPlansNote">{t('plans.note')}</p>
        </section>

        <section className="libSection" id="neighborhood">
          <div className="libSectionHead">
            <h2>{t('neighborhood.title')}</h2>
          </div>
          <div className="libSplit">
            <div className="libProse">
              <p>{t('neighborhood.p1')}</p>
              <p>{t('neighborhood.p2')}</p>
            </div>
            <aside className="libAskList">
              <h3>{t('askList.title')}</h3>
              <ul>
                {['askList.1', 'askList.2', 'askList.3', 'askList.4'].map((key) => (
                  <li key={key}>
                    <button
                      type="button"
                      className="libAskItem"
                      onClick={() => openChat({ message: t(key), source: 'ask_list' })}>
                      {t(key)}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </section>
      </main>

      <section className="libCta">
        <div className="libCtaInner">
          <h2>{t('cta.title')}</h2>
          <p>{t('cta.lede')}</p>
          <div className="libChips">
            {['cta.chip1', 'cta.chip2', 'cta.chip3'].map((key) => (
              <button
                key={key}
                className="libChip"
                onClick={() => openChat({ message: t(key), source: 'cta_chip' })}>
                {t(key)}
              </button>
            ))}
          </div>
          <button className="libBtn libBtnLight" onClick={() => openChat({ source: 'cta' })}>
            {t('cta.start')}
          </button>
        </div>
      </section>

      <footer className="libFooter">
        <div className="libFooterInner">
          <div className="libFooterMark">Lib Mont-Saint-Hilaire</div>
          <p>{t('footer.blurb')}</p>
          <p className="libFooterFine">{t('footer.fine')}</p>
        </div>
      </footer>

      <ProactiveNudge />
    </div>
  );
}

// Bump whenever public/map/index.html is re-vendored: busts browser caches on
// the iframe URL so visitors always get the latest map build.
const MAP_BUILD_VERSION = '2026-08-03-mobile-ui-v3';

/**
 * "The Neighborhood" page — a full-screen embed of the interactive POI map
 * (public/map/index.html, the Le Lib MSH map engine with the agent bridge).
 * The iframe registers with poiMapBridge so the agent's PoiMapControl
 * envelopes can drive it (nearest grocery, schools nearby, etc.). The chat
 * stays mounted at the App level, floating over the map.
 */
function NeighborhoodPage() {
  const [lang] = useAwacLang();
  const frameRef = useRef(null);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Route marker for launcher positioning: on phones the floating chat
  // bubble must ride ABOVE the map's bottom-sheet peek instead of covering
  // its search field (html[data-awac-route="map"] rules in App.css).
  useEffect(() => {
    document.documentElement.dataset.awacRoute = 'map';
    return () => {
      delete document.documentElement.dataset.awacRoute;
    };
  }, []);

  // Keep the map's language in sync with the site's FR/EN toggle.
  useEffect(() => {
    setMapLanguage(lang);
  }, [lang]);

  // Register the iframe with the bridge; onLoad re-registers (and pings) in
  // case the map finished loading before this effect ran.
  useEffect(() => {
    if (frameRef.current && frameRef.current.contentWindow) {
      registerMapFrame(frameRef.current.contentWindow);
    }
    return () => unregisterMapFrame();
  }, []);

  return (
    <div className="lib libMapPage">
      <nav className="libNav" aria-label="Main">
        <a className="libWordmark" href="#/">
          Le Lib<span className="libWordmarkFull"> Mont-Saint-Hilaire</span>
        </a>
        <div className="libNavLinks">
          <a href="#/">{t('nav.backHome')}</a>
          <a href="#/units">{t('nav.unitsTab')}</a>
          <a href="#/neighborhood" className="libNavActive" aria-current="page">
            {t('nav.neighborhoodTab')}
          </a>
          <LanguageToggle />
          <button className="libBtn libBtnPrimary" onClick={() => openChat({ source: 'map_nav' })}>
            <span className="libChatFull">{t('nav.chat')}</span>
            <span className="libChatShort">{t('nav.chatShort')}</span>
          </button>
        </div>
        <MobileNavPages active="map" />
      </nav>
      <div className="libMapFrameWrap">
        <iframe
          ref={frameRef}
          className="libMapFrame"
          src={`map/index.html?v=${MAP_BUILD_VERSION}`}
          title={t('poiMap.iframeTitle')}
          onLoad={() => {
            if (frameRef.current && frameRef.current.contentWindow) {
              registerMapFrame(frameRef.current.contentWindow);
            }
          }}
        />
      </div>
    </div>
  );
}

// Units-page pounce tuning: how long each behavioral signal must hold before
// Lia proactively offers help. Card hover is the strongest signal (shortest
// fuse); the generic dwell fallback also covers mobile, where hover and
// filter taps may never fire.
const POUNCE_HOVER_MS = 2500;
const POUNCE_FILTER_MS = 8000;
const POUNCE_DWELL_MS = 20000;

/**
 * Proactive engagement ("pounce") for the Units page. Watches three
 * behavioral signals, strongest first, and shows ONE contextual nudge per
 * session:
 *   1. hovering a specific unit card ≥2.5s → unit-specific invite whose CTA
 *      sends "Tell me about unit X" (bullet summary + photo carousel),
 *   2. using the bedroom filter → 8s later, a "help me narrow it down" invite,
 *   3. 20s on the page → generic invite (mobile-friendly fallback).
 * Suppressed once the visitor opens the chat themselves; dismissal sticks for
 * the session (same conventions as the landing page's ProactiveNudge).
 * @param {{bedFilter: (string|number), visibleCount: number}} props
 */
function UnitsPounce({ bedFilter, visibleCount }) {
  useAwacLang();
  const [pounce, setPounce] = useState(null); // {kind, text, cta, message}
  const firedRef = useRef(false);
  const hoverTimerRef = useRef(null);
  const filterTimerRef = useRef(null);
  const filterTouchedRef = useRef(false);
  const contextRef = useRef({ bedFilter, visibleCount });
  contextRef.current = { bedFilter, visibleCount };

  function fire(kind, unit) {
    if (firedRef.current || getAwacState('unitsPounceDismissed')) {
      return;
    }
    firedRef.current = true;
    const { bedFilter: beds, visibleCount: count } = contextRef.current;
    let next;
    if (kind === 'unit' && unit) {
      next = {
        kind,
        text: t('pounce.unit', { unit }),
        cta: t('pounce.unitCta', { unit }),
        message: t('unit.askMessage', { unit }),
      };
    } else if (kind === 'filtered') {
      next = {
        kind,
        text: t('pounce.filtered', { count }),
        cta: t('pounce.filteredCta'),
        message: t('pounce.filteredMessage', { beds }),
      };
    } else {
      next = {
        kind: 'dwell',
        text: t('pounce.dwell'),
        cta: t('pounce.dwellCta'),
        message: t('pounce.dwellMessage'),
      };
    }
    setPounce(next);
    track(AwacEvents.PROACTIVE_SHOWN, { page: 'units', trigger: kind, unit: unit || undefined });
  }

  // Signal 1 — unit-card hover dwell (event delegation; cards carry data-unit).
  useEffect(() => {
    function onOver(e) {
      const card = e.target.closest && e.target.closest('.libUnitCard');
      if (!card || firedRef.current) return;
      clearTimeout(hoverTimerRef.current);
      const unit = card.getAttribute('data-unit');
      hoverTimerRef.current = setTimeout(() => fire('unit', unit), POUNCE_HOVER_MS);
    }
    function onOut(e) {
      const card = e.target.closest && e.target.closest('.libUnitCard');
      if (card) clearTimeout(hoverTimerRef.current);
    }
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      clearTimeout(hoverTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Signal 2 — the bedroom filter was used (skip the initial value).
  useEffect(() => {
    if (!filterTouchedRef.current) {
      filterTouchedRef.current = true;
      return undefined;
    }
    clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => fire('filtered'), POUNCE_FILTER_MS);
    return () => clearTimeout(filterTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bedFilter]);

  // Signal 3 — page dwell fallback; also arm the chat-open suppression.
  useEffect(() => {
    if (getAwacState('unitsPounceDismissed')) {
      firedRef.current = true;
      return undefined;
    }
    const dwellId = setTimeout(() => fire('dwell'), POUNCE_DWELL_MS);
    function onChatOpened() {
      firedRef.current = true;
      setPounce(null);
      setAwacState('unitsPounceDismissed', true);
    }
    window.addEventListener('awac:open-chat', onChatOpened);
    return () => {
      clearTimeout(dwellId);
      window.removeEventListener('awac:open-chat', onChatOpened);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!pounce) {
    return null;
  }

  function handleEngage() {
    setPounce(null);
    setAwacState('unitsPounceDismissed', true);
    track(AwacEvents.PROACTIVE_ENGAGED, { page: 'units', trigger: pounce.kind });
    openChat({ message: pounce.message, source: 'units_pounce' });
  }

  function handleDismiss() {
    setPounce(null);
    setAwacState('unitsPounceDismissed', true);
  }

  return (
    <div className="libNudge" role="dialog" aria-label={pounce.text}>
      <p className="libNudgeText">{pounce.text}</p>
      <div className="libNudgeActions">
        <button type="button" className="libNudgeCta" onClick={handleEngage}>
          {pounce.cta}
        </button>
        <button type="button" className="libNudgeDismiss" onClick={handleDismiss}>
          {t('nudge.dismiss')}
        </button>
      </div>
    </div>
  );
}

/**
 * "Units" page — the full inventory of homes, straight from the org's
 * Advertesting_Unit__c records (baked into src/unitsData.json at build time).
 * The agent auto-navigates the visitor here whenever it shows units or floor
 * plans (see the 'awac:show-units' listener in App).
 */
function UnitsPage() {
  const [lang] = useAwacLang();

  // Route marker: the phone Lia bar swaps its placeholder here, and the
  // phone nudge stays Home-only (html[data-awac-route='units'] CSS).
  useEffect(() => {
    document.documentElement.dataset.awacRoute = 'units';
    return () => {
      delete document.documentElement.dataset.awacRoute;
    };
  }, []);

  // When the agent's unit carousel triggered the navigation here with an
  // unambiguous bedroom count, arrive pre-filtered to the same set.
  const [bedFilter, setBedFilter] = useState(() => {
    const pending = Number(getAwacState('unitsFilterBeds'));
    if ([1, 2, 3].includes(pending)) {
      clearAwacState('unitsFilterBeds');
      return pending;
    }
    return 'all';
  }); // 'all' | 1 | 2 | 3 | 'den'
  const [sortBy, setSortBy] = useState('unit');      // 'unit' | 'priceAsc' | 'priceDesc'

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Also react to carousel envelopes that arrive while this page is mounted.
  useEffect(() => {
    function onShowUnits(e) {
      const beds = e && e.detail && Number(e.detail.beds);
      if ([1, 2, 3].includes(beds)) {
        setBedFilter(beds);
      }
    }
    window.addEventListener('awac:show-units', onShowUnits);
    return () => window.removeEventListener('awac:show-units', onShowUnits);
  }, []);

  const units = UNITS.filter((u) => {
    if (bedFilter === 'all') return true;
    if (bedFilter === 'den') return u.den;
    return u.beds === bedFilter;
  }).sort((a, b) => {
    if (sortBy === 'priceAsc') return a.price - b.price;
    if (sortBy === 'priceDesc') return b.price - a.price;
    return String(a.unit).localeCompare(String(b.unit), undefined, { numeric: true });
  });

  const fmtSqft = (v) => (v ? v.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA') : '—');

  return (
    <div className="lib">
      <nav className="libNav" aria-label="Main">
        <a className="libWordmark" href="#/">
          Le Lib<span className="libWordmarkFull"> Mont-Saint-Hilaire</span>
        </a>
        <div className="libNavLinks">
          <a href="#/">{t('nav.backHome')}</a>
          <a href="#/units" className="libNavActive" aria-current="page">{t('nav.unitsTab')}</a>
          <a href="#/neighborhood">{t('nav.neighborhoodTab')}</a>
          <LanguageToggle />
          <button className="libBtn libBtnPrimary" onClick={() => openChat({ source: 'units_nav' })}>
            <span className="libChatFull">{t('nav.chat')}</span>
            <span className="libChatShort">{t('nav.chatShort')}</span>
          </button>
        </div>
        <MobileNavPages active="units" />
      </nav>

      <main>
        <section className="libSection" id="units">
          <div className="libSectionHead">
            <h2>{t('unitsPage.title')}</h2>
            <p>{t('unitsPage.lede', { count: UNITS.length })}</p>
          </div>

          <div className="libUnitControls">
            <div className="libUnitFilters" role="group" aria-label={t('unitsPage.filterLabel')}>
              {[
                { key: 'all', label: t('unitsPage.filter.all') },
                { key: 1, label: t('unitsPage.filter.oneBed') },
                { key: 2, label: t('unitsPage.filter.twoBed') },
                { key: 3, label: t('unitsPage.filter.threeBed') },
                { key: 'den', label: t('unitsPage.filter.den') },
              ].map((f) => (
                <button
                  key={String(f.key)}
                  type="button"
                  className={'libChip libUnitChip' + (bedFilter === f.key ? ' libUnitChipActive' : '')}
                  aria-pressed={bedFilter === f.key}
                  onClick={() => setBedFilter(f.key)}>
                  {f.label}
                </button>
              ))}
            </div>
            <label className="libUnitSort">
              {t('unitsPage.sortLabel')}
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="unit">{t('unitsPage.sort.unit')}</option>
                <option value="priceAsc">{t('unitsPage.sort.priceAsc')}</option>
                <option value="priceDesc">{t('unitsPage.sort.priceDesc')}</option>
              </select>
            </label>
          </div>

          <p className="libUnitCount">{t('unitsPage.count', { shown: units.length, total: UNITS.length })}</p>

          <div className="libUnitGrid">
            {units.map((u) => (
              <article className="libUnitCard" key={u.unit} data-unit={u.unit}>
                <header className="libUnitCardHead">
                  <h3>{t('unit.name', { unit: u.unit })}</h3>
                  <span className="libUnitPlan">{u.plan}</span>
                </header>
                <p className="libUnitSpecs">
                  {u.beds} {t('plan.bed')} · {u.baths} {t('plan.bath')}
                  {u.den ? ` · ${t('unit.den')}` : ''}
                  {u.corner ? ` · ${t('unit.corner')}` : ''}
                </p>
                <p className="libUnitMeta">
                  {fmtSqft(u.sqft)} {t('plan.sqft')} {t('unit.interior')}
                  {u.sqftOut ? ` · ${fmtSqft(u.sqftOut)} ${t('plan.sqft')} ${t('unit.exterior')}` : ''}
                  {` · ${t('unit.floor', { floor: u.floor })}`}
                </p>
                <p className="libUnitView">{lang === 'fr' ? (u.descFr || u.desc) : (u.desc || u.descFr)}</p>
                <footer className="libUnitCardFoot">
                  <strong className="libUnitPrice">{formatPrice(u.price, lang)}{t('unit.perMonth')}</strong>
                  <button
                    type="button"
                    className="libUnitAsk"
                    onClick={() => openChat({ message: t('unit.askMessage', { unit: u.unit }), source: 'unit_card' })}>
                    {t('unit.ask')}
                  </button>
                </footer>
              </article>
            ))}
          </div>
          <p className="libPlansNote">{t('plans.note')}</p>
        </section>
      </main>
      <UnitsPounce bedFilter={bedFilter} visibleCount={units.length} />
    </div>
  );
}

/**
 * Transient toast — makes agent-driven navigation and map results legible
 * ("📍 Lia is taking you to the neighborhood", "📍 Costco – LaSalle · 12.1 km").
 * Fired via the 'awac:toast' CustomEvent (poiMapBridge + the App nav handlers).
 */
function Toast() {
  useAwacLang();
  const [text, setText] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    function fmtDist(km) {
      if (typeof km !== 'number' || Number.isNaN(km)) return '';
      return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
    }
    function onToast(e) {
      const d = (e && e.detail) || {};
      let next = null;
      if (d.kind === 'navMap') next = t('toast.mapNav');
      else if (d.kind === 'navUnits') next = t('toast.unitsNav');
      else if (d.kind === 'mapFound') next = t('toast.mapFound', { name: d.name, dist: fmtDist(d.km) });
      else if (d.kind === 'mapCount') next = t('toast.mapCount', { count: d.count });
      else if (d.kind === 'mapNone') next = t('toast.mapNone');
      if (!next) return;
      setText(next);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setText(null), 4200);
    }
    window.addEventListener('awac:toast', onToast);
    return () => {
      window.removeEventListener('awac:toast', onToast);
      clearTimeout(timerRef.current);
    };
  }, []);

  if (!text) {
    return null;
  }
  return (
    <div className="libToast" role="status" aria-live="polite">
      {text}
    </div>
  );
}

/** Hash-based route: '#/neighborhood' → map page, '#/units' → units page. */
function getRoute() {
  const hash = window.location.hash;
  if (hash.indexOf('#/neighborhood') === 0) return 'neighborhood';
  if (hash.indexOf('#/units') === 0) return 'units';
  return 'home';
}

function App() {
  // AWAC demo harness: visit /?demo=awac to see the expand-and-render flow
  // without a live Salesforce connection. The production app is unaffected.
  // ?demo=awac forces the demo; ?app=1 forces the real client.
  // REACT_APP_DEFAULT_DEMO=1 (set for the Cloudflare/preview demo build) makes the
  // demo the default landing view without needing the query param.
  const params = new URLSearchParams(window.location.search);
  const defaultDemo = process.env.REACT_APP_DEFAULT_DEMO === '1';
  const wantsDemo = params.get('demo') === 'awac' || (defaultDemo && params.get('app') !== '1');

  const [route, setRoute] = useState(getRoute);

  // Navigation-aware agent: keep the page-context tag in sync with the route.
  useEffect(() => {
    setCurrentPage(route);
  }, [route]);

  useEffect(() => {
    function onHashChange() {
      setRoute(getRoute());
    }
    // The poiMapBridge fires this when the agent sends a map command from any
    // page: navigate to the Neighborhood page so the map is visible.
    function onShowNeighborhood() {
      if (getRoute() !== 'neighborhood') {
        window.location.hash = '#/neighborhood';
        window.dispatchEvent(new CustomEvent('awac:toast', { detail: { kind: 'navMap' } }));
      }
    }
    // conversation.js fires this when the agent shows units / floor plans:
    // take the visitor to the Units page automatically.
    function onShowUnits() {
      if (getRoute() !== 'units') {
        window.location.hash = '#/units';
        window.dispatchEvent(new CustomEvent('awac:toast', { detail: { kind: 'navUnits' } }));
      }
    }
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('awac:show-neighborhood', onShowNeighborhood);
    window.addEventListener('awac:show-units', onShowUnits);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('awac:show-neighborhood', onShowNeighborhood);
      window.removeEventListener('awac:show-units', onShowUnits);
    };
  }, []);

  if (wantsDemo) {
    return <AwacDemo />;
  }

  // BootstrapMessaging lives at the App level (not inside a page) so the chat
  // window and its SSE connection survive navigation between pages.
  return (
    <>
      {route === 'neighborhood' ? <NeighborhoodPage /> : route === 'units' ? <UnitsPage /> : <LandingPage />}
      <Toast />
      <BootstrapMessaging />
    </>
  );
}

export default App;
